import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CalendarDays, Download, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { StatCard, Card, Spinner, ICONS, EmptyState } from "../components/dashboard/DashboardShell";
import OrdersTable from "../components/dashboard/OrdersTable";
import { api } from "../lib/api";
import { rupiah, timeAgo, fmtDate } from "../lib/format";

const LINE_COLORS = { "Mobil Bekas": "#2563eb", "Mobil Listrik": "#7c3aed", "Truk": "#f59e0b", "Motor Moge": "#0f172a" };
const ACT_META = { inspector_new: ["usercheck", "text-blue-600 bg-blue-50"], order_new: ["clipboard", "text-amber-600 bg-amber-50"], report: ["file", "text-blue-600 bg-blue-50"], payout: ["banknote", "text-emerald-600 bg-emerald-50"], payout_paid: ["banknote", "text-emerald-600 bg-emerald-50"], user_new: ["users", "text-violet-600 bg-violet-50"], payment: ["wallet", "text-emerald-600 bg-emerald-50"], verification: ["usercheck", "text-indigo-600 bg-indigo-50"], order_done: ["check", "text-emerald-600 bg-emerald-50"], document: ["filecheck", "text-slate-600 bg-slate-100"] };

const short = (n) => n >= 1e9 ? `Rp ${(n / 1e9).toFixed(2)} M` : n >= 1e6 ? `Rp ${(n / 1e6).toFixed(1)} Jt` : rupiah(n);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { data: d, isLoading } = useQuery({ queryKey: ["admin-dashboard"], queryFn: () => api.get("/admin/dashboard").then((r) => r.data) });
  if (isLoading || !d) return <Spinner />;
  const s = d.stats;
  const stats = [
    { label: "Total Pengguna", value: s.total_users, icon: "users", color: "text-blue-600 bg-blue-50", testId: "stat-users" },
    { label: "Total Inspector", value: s.total_inspectors, icon: "usercheck", color: "text-red-500 bg-red-50", testId: "stat-inspectors" },
    { label: "Total Pesanan", value: s.total_orders, icon: "clipboard", color: "text-emerald-600 bg-emerald-50", testId: "stat-orders" },
    { label: "Total Transaksi", value: short(s.total_transactions), icon: "wallet", color: "text-amber-600 bg-amber-50", testId: "stat-tx" },
    { label: "Pendapatan Platform", value: short(s.platform_income), icon: "coins", color: "text-emerald-600 bg-emerald-50", testId: "stat-income" },
    { label: "Inspector Menunggu Verifikasi", value: s.pending_verification, icon: "bell", color: "text-amber-600 bg-amber-50", testId: "stat-pending-verif" },
  ];
  const maxRegion = Math.max(...d.region_spread.map((r) => r.jumlah), 1);
  const exportCsv = () => {
    const rows = [["order_id", "tanggal", "jenis", "pengguna", "inspector", "status", "harga"], ...d.recent_orders.map((o) => [o.order_id, o.created_at, o.vehicle_type_label, o.user?.name, o.inspector?.name, o.status, o.price])];
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "inspecar-pesanan.csv"; a.click();
    toast.success("Data pesanan diekspor (CSV)");
  };

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Dashboard Super Admin</h1>
          <p className="text-sm text-slate-500 mt-1">Selamat datang, Admin. Pantau seluruh aktivitas platform Inspecar secara real-time.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg px-3.5 py-2.5"><CalendarDays size={15} className="text-slate-400" /> 30 hari terakhir</span>
          <Button onClick={exportCsv} data-testid="export-btn" className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs h-10 px-4 shadow-lg shadow-blue-600/25"><Download size={14} className="mr-1.5" /> Export Data</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mt-6">{stats.map((st) => <StatCard key={st.label} {...st} />)}</div>

      <div className="grid xl:grid-cols-3 gap-4 mt-4">
        <Card title="Grafik Pesanan">
          <div className="flex flex-wrap gap-3 mb-3">
            {Object.entries(LINE_COLORS).map(([k, c]) => <span key={k} className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: c }} /> {k}</span>)}
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="hari" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} width={24} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                {Object.entries(LINE_COLORS).map(([k, c]) => <Line key={k} type="monotone" dataKey={k} stroke={c} strokeWidth={2} dot={false} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card title="Pesanan Terbaru" link="Lihat Semua" onLink={() => navigate("/admin/pesanan")}>
          <OrdersTable orders={d.recent_orders} compact />
        </Card>
        <Card title="Sebaran Inspector" link="Lihat Detail" onLink={() => navigate("/admin/inspector")}>
          <div className="relative h-28 rounded-xl bg-gradient-to-b from-blue-50 to-slate-50 border border-slate-100 mb-4 overflow-hidden">
            <svg viewBox="0 0 300 100" className="absolute inset-0 w-full h-full opacity-70"><path d="M20 55 Q 50 40 80 52 T 150 58 Q 180 45 210 55 T 280 60" stroke="#bfdbfe" strokeWidth="18" fill="none" strokeLinecap="round" /><path d="M60 75 Q 120 68 190 78" stroke="#dbeafe" strokeWidth="10" fill="none" strokeLinecap="round" /></svg>
            {[[45, 40], [90, 55], [130, 45], [165, 60], [205, 50], [240, 62]].slice(0, d.region_spread.length).map(([x, y], i) => <MapPin key={i} size={16} className="absolute text-blue-600" fill="#2563eb" style={{ left: `${x / 3}%`, top: `${y}%` }} />)}
          </div>
          <div className="space-y-2.5">
            {d.region_spread.map((r) => (
              <div key={r.wilayah} className="flex items-center gap-3">
                <span className="text-xs font-semibold text-slate-600 w-24 truncate">{r.wilayah}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-600 rounded-full" style={{ width: `${(r.jumlah / maxRegion) * 100}%` }} /></div>
                <span className="text-xs font-bold text-slate-700 w-8 text-right">{r.jumlah}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card title="Aktivitas Terbaru" className="mt-4">
        {!d.activities.length ? <EmptyState icon="bell" title="Belum ada aktivitas" /> : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="activities">
            {d.activities.map((a) => {
              const [icon, color] = ACT_META[a.type] || ["bell", "text-slate-600 bg-slate-100"];
              const Icon = ICONS[icon];
              return (
                <div key={a.activity_id} className="flex items-start gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors">
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}><Icon size={17} /></span>
                  <div className="min-w-0"><p className="text-[11px] text-slate-400 font-medium">{a.title}</p><p className="text-xs font-bold text-slate-800 truncate">{a.desc}</p><p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(a.created_at)}</p></div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
